import { supabase } from './supabase';

class AuthService {
  private isAuthenticated = false;
  private authCheckPromise: Promise<boolean> | null = null;
  private lastGoogleUserId: string | null = null;
  private identityExpiredHandled = false;

  /**
   * Automatischer Login über Backend Function
   * @param googleUserId - Optional: Google User ID für Logging
   */
  async autoLogin(googleUserId?: string): Promise<boolean> {
    // Speichere Google User ID für späteres Logging
    if (googleUserId) {
      this.lastGoogleUserId = googleUserId;
    }

    // Verhindere mehrfache Login-Versuche
    if (this.authCheckPromise) {
      return this.authCheckPromise;
    }

    this.authCheckPromise = this.performAutoLogin();
    return this.authCheckPromise;
  }

  /**
   * Wird aufgerufen, wenn das App-Token aus dem Google-Login fehlt oder
   * abgelaufen ist. Entfernt den Token und lädt die Anwendung neu. Beim
   * Neuaufbau findet useAuth keinen Token mehr, der Benutzer landet auf der
   * Anmeldeseite statt vor einer Oberfläche, die keine Daten mehr lädt.
   */
  private handleIdentityExpired(): void {
    if (this.identityExpiredHandled) {
      return;
    }

    this.identityExpiredHandled = true;
    this.isAuthenticated = false;

    console.warn('App-Token fehlt oder ist abgelaufen, Rückkehr zur Anmeldung');

    localStorage.removeItem('authToken');
    window.location.reload();
  }

  private async performAutoLogin(): Promise<boolean> {
    try {
      // Prüfe ob bereits eine Session existiert
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        this.isAuthenticated = true;
        console.log('Existing Supabase session found');
        return true;
      }

      // Identität aus dem Google-Login: Ohne diesen Token gibt die Backend
      // Function keine Datenbanksitzung heraus.
      const identityToken = localStorage.getItem('authToken');

      if (!identityToken) {
        this.handleIdentityExpired();
        return false;
      }

      // Rufe Backend Function für sicheren Login auf
      const response = await fetch('/.netlify/functions/supabase-auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${identityToken}`
        }
      });

      // 401 bedeutet abgelaufene oder ungültige Identität. Andere Fehler sind
      // echte Störungen und behalten die bestehende Fehlerbehandlung.
      if (response.status === 401) {
        this.handleIdentityExpired();
        return false;
      }

      if (!response.ok) {
        const error = await response.json();
        console.error('Supabase auth function failed:', error);
        return false;
      }

      const result = await response.json();

      if (result.success && result.session) {
        // Setze Session manuell
        await supabase.auth.setSession({
          access_token: result.session.access_token,
          refresh_token: result.session.refresh_token
        });

        this.isAuthenticated = true;
        console.log('Supabase auto-login successful via backend');
        
        // Hier könntest du später ein Logging implementieren
        if (this.lastGoogleUserId) {
          console.log(`User ${this.lastGoogleUserId} authenticated via shared Supabase account`);
        }
        
        return true;
      }

      return false;
    } catch (error) {
      console.error('Supabase auto-login error:', error);
      return false;
    } finally {
      this.authCheckPromise = null;
    }
  }

  /**
   * Prüft ob User authentifiziert ist
   */
  async checkAuth(): Promise<boolean> {
    if (this.isAuthenticated) {
      return true;
    }

    const { data: { session } } = await supabase.auth.getSession();
    this.isAuthenticated = !!session;
    
    return this.isAuthenticated;
  }

  /**
   * Session erneuern wenn nötig
   */
  async refreshSession(): Promise<void> {
    try {
      const { error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('Session refresh failed:', error);
        // Bei Fehler neu einloggen
        await this.autoLogin();
      }
    } catch (error) {
      console.error('Session refresh error:', error);
    }
  }

  /**
   * Auth State Listener einrichten
   */
  setupAuthListener() {
    supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Supabase auth state changed:', event);
      
      if (event === 'SIGNED_OUT' || !session) {
        this.isAuthenticated = false;
        // Automatisch wieder einloggen
        await this.autoLogin();
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        this.isAuthenticated = true;
      }
    });
  }

  /**
   * Hole die aktuelle Google User ID (für Logging)
   */
  getLastGoogleUserId(): string | null {
    return this.lastGoogleUserId;
  }
}

export const authService = new AuthService();
